import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronUp, ChevronDown, ExternalLink, Save, Loader2, Upload, Image as ImageIcon, X } from "lucide-react";
import api, { formatErr, fileUrl, getToken } from "../lib/apiClient";
import { fmtCAD } from "../lib/format";

/**
 * Settings → Invoices page.
 *
 * HouseCall Pro–style tab layout: Configuration · Automations · Customer view · Email and SMS.
 * For Item 2, only the "Customer view" tab is fully implemented — it configures the
 * default field visibility, view format, and default payment terms that apply to every
 * new invoice + the Send-Invoice preview page.
 *
 * The other tabs surface as stubs pointing users to the existing legacy /settings page
 * until Item 9 rebuilds them properly.
 */

const TABS = [
  { key: "configuration", label: "Configuration" },
  { key: "automations", label: "Automations" },
  { key: "customer_view", label: "Customer view" },
  { key: "email_sms", label: "Email and SMS" },
];

export const DEFAULT_CUSTOMER_VIEW = {
  sections: {
    payment: true,
    jobAndInvoice: true,
    viewFormat: true,
    businessAndCustomer: true,
    services: true,
    materials: true,
  },
  fields: {
    // Job and invoice
    jobNumber: true,
    invoiceNumber: true,
    serviceDate: true,
    invoiceDate: true,
    summaryOfWork: true,
    // Business and customer
    businessName: true,
    technicianName: false,
    customerDisplayName: true,
    customerCompanyName: false,
    // Services
    servicesLineItems: true,
    servicesName: true,
    servicesDescription: true,
    servicesQuantity: false,
    servicesUnitPrice: true,
    servicesAmount: true,
    servicesSubtotal: true,
    // Materials
    materialsLineItems: false,
    materialsSubtotal: false,
  },
  viewFormat: "email",
};

export function mergeCustomerView(fromServer) {
  const src = fromServer || {};
  return {
    sections: { ...DEFAULT_CUSTOMER_VIEW.sections, ...(src.sections || {}) },
    fields: { ...DEFAULT_CUSTOMER_VIEW.fields, ...(src.fields || {}) },
    viewFormat: src.viewFormat || DEFAULT_CUSTOMER_VIEW.viewFormat,
  };
}

export default function SettingsInvoices() {
  const [activeTab, setActiveTab] = useState("configuration");
  const [settings, setSettings] = useState(null);
  const [prefs, setPrefs] = useState(DEFAULT_CUSTOMER_VIEW);
  const [defaultTerms, setDefaultTerms] = useState({ mode: "upon", uponValue: "receipt", netDays: 30 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
        setPrefs(mergeCustomerView(data.invoice_customer_view));
        // Decompose default_payment_terms into radio + value
        const t = data.default_payment_terms || "C.O.D.";
        if (t.startsWith("Net")) {
          setDefaultTerms({ mode: "net", uponValue: "receipt", netDays: Number(t.replace("Net ", "")) || 30 });
        } else if (t === "Due on Receipt") {
          setDefaultTerms({ mode: "upon", uponValue: "receipt", netDays: 30 });
        } else if (t === "C.O.D.") {
          setDefaultTerms({ mode: "upon", uponValue: "C.O.D.", netDays: 30 });
        } else {
          setDefaultTerms({ mode: "upon", uponValue: "receipt", netDays: 30 });
        }
      } catch (e) {
        setErr(formatErr(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const composedTerms = useMemo(() => {
    if (defaultTerms.mode === "net") return `Net ${Number(defaultTerms.netDays) || 30}`;
    if (defaultTerms.uponValue === "C.O.D.") return "C.O.D.";
    return "Due on Receipt";
  }, [defaultTerms]);

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await api.put("/settings", {
        ...settings,
        default_payment_terms: composedTerms,
        invoice_customer_view: prefs,
      });
      setSavedAt(new Date().toISOString());
      setTimeout(() => setSavedAt(null), 3000);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setPrefs((p) => ({ ...p, fields: { ...p.fields, [k]: v } }));
  const setSection = (k, v) => setPrefs((p) => ({ ...p, sections: { ...p.sections, [k]: v } }));
  const setViewFormat = (v) => setPrefs((p) => ({ ...p, viewFormat: v }));

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500 dark:text-slate-400">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading…
      </div>
    );
  }

  return (
    <div data-testid="settings-invoices-page" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Invoices</h1>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-800">
        <nav className="flex gap-8" data-testid="settings-invoices-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`settings-invoices-tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              className={`px-1 pb-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === t.key
                  ? "border-[#0F4C81] text-[#0F4C81]"
                  : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 px-3 py-2 rounded" data-testid="settings-invoices-error">{err}</div>}

      {activeTab === "customer_view" && (
        <CustomerViewTab
          prefs={prefs}
          settings={settings}
          defaultTerms={defaultTerms}
          setDefaultTerms={setDefaultTerms}
          setField={setField}
          setSection={setSection}
          setViewFormat={setViewFormat}
          save={save}
          saving={saving}
          savedAt={savedAt}
        />
      )}

      {activeTab === "configuration" && (
        <ConfigurationTab
          settings={settings}
          setSettings={setSettings}
          save={save}
          saving={saving}
          savedAt={savedAt}
          reload={async () => {
            const { data } = await api.get("/settings");
            setSettings(data);
          }}
        />
      )}

      {activeTab !== "customer_view" && activeTab !== "configuration" && (
        <StubTab tab={activeTab} />
      )}
    </div>
  );
}

/* --------------- Customer view tab --------------- */

function CustomerViewTab({ prefs, settings, defaultTerms, setDefaultTerms, setField, setSection, setViewFormat, save, saving, savedAt }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-bold">Customer view</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Configure the default contents shown on all future invoices. One-off adjustments may be made when sending.</p>
      </div>

      {/* Sample invoice preview */}
      <SamplePreview prefs={prefs} settings={settings} defaultTerms={defaultTerms} />

      {/* Default terms */}
      <SettingsPanel title="Default terms" testId="section-default-terms">
        <div className="space-y-4">
          <div className="grid sm:grid-cols-[220px_1fr] gap-4 items-center">
            <label className="flex items-center gap-2.5 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
              <input
                type="radio"
                name="default-terms-mode"
                value="upon"
                checked={defaultTerms.mode === "upon"}
                onChange={() => setDefaultTerms({ ...defaultTerms, mode: "upon" })}
                className="w-4 h-4 accent-[#0F4C81]"
                data-testid="default-terms-upon"
              />
              Upon
            </label>
            <select
              value={defaultTerms.uponValue}
              onChange={(e) => setDefaultTerms({ ...defaultTerms, uponValue: e.target.value })}
              disabled={defaultTerms.mode !== "upon"}
              className="w-full max-w-md px-3 py-2 border border-slate-300 dark:border-slate-700 rounded bg-white dark:bg-slate-900 text-sm disabled:opacity-50"
              data-testid="default-terms-upon-value"
            >
              <option value="receipt">receipt</option>
              <option value="C.O.D.">C.O.D.</option>
            </select>
          </div>
          <div className="grid sm:grid-cols-[220px_1fr] gap-4 items-center">
            <label className="flex items-center gap-2.5 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
              <input
                type="radio"
                name="default-terms-mode"
                value="net"
                checked={defaultTerms.mode === "net"}
                onChange={() => setDefaultTerms({ ...defaultTerms, mode: "net" })}
                className="w-4 h-4 accent-[#0F4C81]"
                data-testid="default-terms-net"
              />
              Net
            </label>
            <select
              value={defaultTerms.netDays}
              onChange={(e) => setDefaultTerms({ ...defaultTerms, netDays: Number(e.target.value) })}
              disabled={defaultTerms.mode !== "net"}
              className="w-full max-w-md px-3 py-2 border border-slate-300 dark:border-slate-700 rounded bg-white dark:bg-slate-900 text-sm disabled:opacity-50"
              data-testid="default-terms-net-days"
            >
              {[15, 30, 45, 60, 90].map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>
      </SettingsPanel>

      {/* Job and invoice */}
      <CollapsiblePanel
        title="Job and invoice"
        open={prefs.sections.jobAndInvoice}
        onToggle={() => setSection("jobAndInvoice", !prefs.sections.jobAndInvoice)}
        testId="section-job-invoice"
      >
        <FieldCheck label="Job number" k="jobNumber" prefs={prefs} setField={setField} />
        <FieldCheck label="Invoice number" k="invoiceNumber" prefs={prefs} setField={setField} />
        <FieldCheck label="Service date" k="serviceDate" prefs={prefs} setField={setField} />
        <FieldCheck label="Invoice date" k="invoiceDate" prefs={prefs} setField={setField} />
        <FieldCheck label="Summary of work" k="summaryOfWork" prefs={prefs} setField={setField} />

        <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800">
          <div className="font-display font-semibold mb-3 text-slate-900 dark:text-slate-100">View format</div>
          <div className="space-y-2.5">
            <label className="flex items-center gap-2.5 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
              <input
                type="radio"
                name="view-format"
                checked={prefs.viewFormat === "email"}
                onChange={() => setViewFormat("email")}
                data-testid="view-format-email"
                className="w-4 h-4 accent-[#0F4C81]"
              />
              Email optimized
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
              <input
                type="radio"
                name="view-format"
                checked={prefs.viewFormat === "envelope"}
                onChange={() => setViewFormat("envelope")}
                data-testid="view-format-envelope"
                className="mt-0.5 w-4 h-4 accent-[#0F4C81]"
              />
              <div>
                <div>Envelope optimized</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">For use with #9 or #10 window envelopes</div>
              </div>
            </label>
          </div>
        </div>
      </CollapsiblePanel>

      {/* Business and customer */}
      <CollapsiblePanel
        title="Business and customer"
        open={prefs.sections.businessAndCustomer}
        onToggle={() => setSection("businessAndCustomer", !prefs.sections.businessAndCustomer)}
        testId="section-business-customer"
      >
        <FieldCheck label="Business name" k="businessName" prefs={prefs} setField={setField} />
        <FieldCheck label="Technician name" k="technicianName" prefs={prefs} setField={setField} />
        <FieldCheck label="Customer display name" k="customerDisplayName" prefs={prefs} setField={setField} />
        <FieldCheck label="Customer company name" k="customerCompanyName" prefs={prefs} setField={setField} />
      </CollapsiblePanel>

      {/* Services */}
      <CollapsiblePanel
        title="Services"
        open={prefs.sections.services}
        onToggle={() => setSection("services", !prefs.sections.services)}
        testId="section-services"
      >
        <FieldCheck label="Line items" k="servicesLineItems" prefs={prefs} setField={setField} />
        <FieldCheck label="Services name" k="servicesName" prefs={prefs} setField={setField} />
        <FieldCheck label="Description" k="servicesDescription" prefs={prefs} setField={setField} />
        <FieldCheck label="Quantity" k="servicesQuantity" prefs={prefs} setField={setField} />
        <FieldCheck label="Unit price" k="servicesUnitPrice" prefs={prefs} setField={setField} />
        <FieldCheck label="Line items amount" k="servicesAmount" prefs={prefs} setField={setField} />
        <FieldCheck label="Subtotal" k="servicesSubtotal" prefs={prefs} setField={setField} />
      </CollapsiblePanel>

      {/* Materials */}
      <CollapsiblePanel
        title="Materials"
        open={prefs.sections.materials}
        onToggle={() => setSection("materials", !prefs.sections.materials)}
        testId="section-materials"
      >
        <FieldCheck label="Line items" k="materialsLineItems" prefs={prefs} setField={setField} />
        <FieldCheck label="Subtotal" k="materialsSubtotal" prefs={prefs} setField={setField} />
      </CollapsiblePanel>

      {/* Footer note */}
      <div className="text-sm text-slate-500 dark:text-slate-400">
        To change the logo and default invoice message, visit <Link to="/settings" className="text-[#0F4C81] hover:underline font-medium">Company profile</Link>.
      </div>

      {/* Save bar */}
      <div className="sticky bottom-0 bg-slate-50 dark:bg-slate-950 -mx-6 px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end gap-3">
        {savedAt && <span className="text-sm text-emerald-600 dark:text-emerald-400" data-testid="settings-invoices-saved">Saved</span>}
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-customer-view-btn"
          className="px-6 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-full font-semibold flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save changes</>}
        </button>
      </div>
    </div>
  );
}

/* --------------- Sample preview card --------------- */

function SamplePreview({ prefs, settings, defaultTerms }) {
  const biz = settings?.business_name || "Any Hour Plumbing";
  const bizAddr = settings?.address || "";
  const bizPhone = settings?.phone || "";
  const bizEmail = settings?.email || "";
  const hstNum = settings?.hst_number || "";
  const footer = (settings?.invoice_footer_message || "").trim();
  const longTerms = (settings?.invoice_terms_long || "").trim();
  const termsUrl = settings?.invoice_terms_url || "";
  const termsText = settings?.invoice_terms_text || "Terms & Conditions";

  const dueLabel = defaultTerms.mode === "net" ? `Net ${defaultTerms.netDays}` : (defaultTerms.uponValue === "C.O.D." ? "C.O.D." : "Upon receipt");
  const sampleDate = "Jul 12, 2026";

  const message = [
    hstNum ? `HST: ${hstNum}` : "",
    footer,
    longTerms,
  ].filter(Boolean).join("\n\n");

  return (
    <div data-testid="settings-invoice-sample-preview" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-8">
        <div>
          {settings?.logo_path ? (
            <img src={fileUrl(settings.logo_path)} alt="" className="h-16 mb-3" />
          ) : (
            <div className="w-16 h-16 border border-slate-300 dark:border-slate-700 rounded flex items-center justify-center text-slate-400 dark:text-slate-500 mb-3">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="16" rx="1"/><circle cx="9" cy="10" r="1.5"/><path d="M4 18l5-6 4 4 3-3 5 6"/></svg>
            </div>
          )}
          {prefs.fields.businessName && <div className="font-display text-xl font-bold">{biz}</div>}
        </div>

        {/* Meta box */}
        <div className="w-[280px] border border-slate-200 dark:border-slate-800 rounded">
          {prefs.fields.jobNumber && <MetaRow label="JOB" value="#12" />}
          {prefs.fields.invoiceNumber && <MetaRow label="INVOICE" value="#1234" />}
          {prefs.fields.serviceDate && <MetaRow label="SERVICE DATE" value={sampleDate} />}
          {prefs.fields.invoiceDate && <MetaRow label="INVOICE DATE" value={sampleDate} />}
          <MetaRow label="PAYMENT TERMS" value={dueLabel} />
          <MetaRow label="DUE DATE" value={sampleDate} />
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">AMOUNT DUE</span>
            <span className="font-display text-lg font-bold tabular">{fmtCAD(401)}</span>
          </div>
        </div>
      </div>

      {/* Customer + service block */}
      <div className="mt-10 grid grid-cols-2 gap-8 text-sm">
        <div className="space-y-1">
          {prefs.fields.customerDisplayName && <div className="font-semibold">John Doe</div>}
          <div className="text-slate-600 dark:text-slate-400">456 State st<br/>California, CA 90265</div>
          <div className="mt-3 flex items-center gap-2 text-slate-600 dark:text-slate-400"><span>📞</span> 555-555-5555</div>
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400"><span>✉</span> john.doe@company.com</div>
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800 pb-1 mb-1">Service Address</div>
            <div className="text-slate-700 dark:text-slate-300">123 Main St<br/>Anytown, CA 90210</div>
          </div>
          {(bizAddr || bizPhone || bizEmail) && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800 pb-1 mb-1">Contact Us</div>
              {bizAddr && <div className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{bizAddr}</div>}
              {bizPhone && <div className="text-slate-700 dark:text-slate-300 mt-1">📞 {bizPhone}</div>}
              {bizEmail && <div className="text-slate-700 dark:text-slate-300">✉ {bizEmail}</div>}
            </div>
          )}
        </div>
      </div>

      {/* INVOICE + services */}
      <div className="mt-10">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">Invoice</div>
        {prefs.fields.servicesLineItems && (
          <>
            <div className="bg-slate-200 dark:bg-slate-800 px-3 py-2 grid grid-cols-[1fr_100px_120px] gap-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
              <div>Services</div>
              {prefs.fields.servicesUnitPrice && <div className="text-right">unit price</div>}
              {prefs.fields.servicesAmount && <div className="text-right">amount</div>}
            </div>
            <div className="border-b border-slate-100 dark:border-slate-800 py-3 grid grid-cols-[1fr_100px_120px] gap-3 text-sm">
              <div>
                {prefs.fields.servicesName && <div className="font-medium">Sample service</div>}
                {prefs.fields.servicesDescription && <div className="text-slate-500 dark:text-slate-400 text-[13px] mt-1">In this sample service...</div>}
              </div>
              {prefs.fields.servicesUnitPrice && <div className="text-right tabular">{fmtCAD(390)}</div>}
              {prefs.fields.servicesAmount && <div className="text-right tabular font-medium">{fmtCAD(390)}</div>}
            </div>
            {prefs.fields.servicesSubtotal && (
              <div className="text-right text-sm mt-2 text-slate-600 dark:text-slate-300">
                Services subtotal: <span className="font-semibold tabular ml-1">{fmtCAD(390)}</span>
              </div>
            )}
          </>
        )}

        {/* Totals */}
        <div className="flex justify-end mt-8">
          <div className="w-[320px] space-y-2">
            <TotalRow label="Subtotal" value={fmtCAD(400)} />
            <TotalRow label="Sample discount name" value={fmtCAD(20)} muted />
            <TotalRow label="Total Tax" value={fmtCAD(19)} />
            <TotalRow label="Sample tax name (5%)" value={fmtCAD(19)} muted />
            <TotalRow label="Job Total" value={fmtCAD(401)} />
            <div className="flex items-baseline justify-between pt-1">
              <span className="font-semibold">Amount Due</span>
              <span className="font-display text-xl font-bold tabular">{fmtCAD(401)}</span>
            </div>
            <TotalRow label="Tip" value={fmtCAD(2)} muted />
          </div>
        </div>
      </div>

      {/* Terms */}
      {message && (
        <div className="mt-10 pt-6 border-t border-slate-200 dark:border-slate-800 text-[13px] leading-relaxed text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
          {message}
        </div>
      )}
      {termsUrl && (
        <div className="mt-6 text-sm text-slate-600 dark:text-slate-400">
          See our <a href={termsUrl} target="_blank" rel="noopener noreferrer" className="text-[#0F4C81] hover:underline font-medium">{termsText}</a>
        </div>
      )}
      <div className="mt-8 pt-4 border-t border-slate-200 dark:border-slate-800 text-xs text-slate-400 dark:text-slate-500">{biz}</div>
    </div>
  );
}

/* --------------- Atoms --------------- */

function SettingsPanel({ title, children, testId }) {
  return (
    <div data-testid={testId} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-6">
      <h3 className="font-display font-bold text-lg mb-4 text-slate-900 dark:text-slate-100">{title}</h3>
      {children}
    </div>
  );
}

function CollapsiblePanel({ title, open, onToggle, testId, children }) {
  return (
    <div data-testid={testId} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-6">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left mb-1"
        aria-expanded={open}
      >
        <h3 className="font-display font-bold text-lg text-slate-900 dark:text-slate-100">{title}</h3>
        {open ? <ChevronUp size={20} className="text-slate-500" /> : <ChevronDown size={20} className="text-slate-500" />}
      </button>
      {open && <div className="mt-4 space-y-3">{children}</div>}
    </div>
  );
}

function FieldCheck({ label, k, prefs, setField }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer text-sm text-slate-700 dark:text-slate-300 select-none">
      <input
        type="checkbox"
        checked={!!prefs.fields[k]}
        onChange={(e) => setField(k, e.target.checked)}
        className="w-4 h-4 rounded accent-[#0F4C81]"
        data-testid={`customer-view-toggle-${k}`}
      />
      {label}
    </label>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-800 last:border-b-0 flex items-center justify-between text-sm">
      <span className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-medium">{label}</span>
      <span className="tabular font-medium">{value}</span>
    </div>
  );
}

function TotalRow({ label, value, muted }) {
  return (
    <div className={`flex items-center justify-between text-sm ${muted ? "text-slate-500 dark:text-slate-400" : "text-slate-800 dark:text-slate-200"}`}>
      <span className={muted ? "pl-4" : ""}>{label}</span>
      <span className="tabular">{value}</span>
    </div>
  );
}

/* --------------- Configuration tab (Item 8) --------------- */

const PRESET_COLORS = [
  { hex: "#0F4C81", label: "Brand blue (default)" },
  { hex: "#1E3A8A", label: "Navy" },
  { hex: "#0369A1", label: "Sky" },
  { hex: "#065F46", label: "Emerald" },
  { hex: "#B45309", label: "Amber" },
  { hex: "#9F1239", label: "Crimson" },
  { hex: "#4C1D95", label: "Purple" },
  { hex: "#1F2937", label: "Charcoal" },
];

function ConfigurationTab({ settings, setSettings, save, saving, savedAt, reload }) {
  const [color, setColor] = useState(settings?.brand_color || "#0F4C81");
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const fileRef = useRef(null);

  const pickColor = (hex) => {
    setColor(hex);
    setSettings({ ...settings, brand_color: hex });
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true); setUploadErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = getToken();
      const res = await fetch(fileUrl("/settings/logo"), {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await reload();
    } catch (e) { setUploadErr(e.message || String(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    setUploading(true); setUploadErr("");
    try {
      await api.put("/settings", { ...settings, logo_path: null });
      await reload();
    } catch (e) { setUploadErr(formatErr(e)); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-6" data-testid="config-tab">
      <div>
        <h2 className="font-display text-xl font-bold">Configuration</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Business logo and brand colour applied across invoices — on-screen preview, send-preview page, and the customer-facing PDF.</p>
      </div>

      {/* Section A.5 · Starting invoice number */}
      <div data-testid="starting-number-panel" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-6">
        <h3 className="font-display font-bold text-lg mb-1 text-slate-900 dark:text-slate-100">Starting invoice number</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          Set a starting invoice/job number so future records begin from there. Existing invoices are not renumbered.
          Leave blank or 0 to keep the current sequence. <strong>The value must be higher than your current highest invoice number</strong> — the system will reject any value that would cause duplicates or go backwards.
        </p>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={0}
            max={999999}
            value={settings.invoice_number_start || 0}
            onChange={(e) => setSettings((s) => ({ ...s, invoice_number_start: parseInt(e.target.value) || 0 }))}
            data-testid="starting-number-input"
            placeholder="e.g. 1000"
            className="w-40 px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-md focus:outline-none focus:border-[#0F4C81] tabular"
          />
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="starting-number-save"
            className="px-4 py-2 text-sm bg-[#0F4C81] hover:bg-[#0A365C] disabled:opacity-50 text-white rounded-md font-medium"
          >{saving ? "Saving…" : "Save"}</button>
          {savedAt && <span className="text-xs text-emerald-700">Saved</span>}
        </div>
        <p className="text-[11px] text-slate-500 mt-2">Currently the sequence will resume from at least this value on the next invoice.</p>
      </div>

      {/* Part 22 (Feb 2026) · Service Address visibility */}
      <div data-testid="service-address-toggle-panel" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-6">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1">
            <h3 className="font-display font-bold text-lg mb-1 text-slate-900 dark:text-slate-100">Show Service Address on invoices</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              When ON, the customer's Service Address appears in the top-right of the invoice
              (both on-screen preview and downloaded PDF). Turn OFF to hide it — useful when the
              billing and service addresses are the same, or you don't want to reprint it.
            </p>
          </div>
          <label className="inline-flex items-center gap-3 cursor-pointer select-none shrink-0">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">
              {(settings.invoice_show_service_address !== false) ? "On" : "Off"}
            </span>
            <input
              type="checkbox"
              data-testid="toggle-show-service-address"
              className="sr-only peer"
              checked={settings.invoice_show_service_address !== false}
              onChange={(e) => setSettings((s) => ({ ...s, invoice_show_service_address: e.target.checked }))}
            />
            <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-checked:bg-[#0F4C81] transition-colors relative">
              <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
            </div>
          </label>
        </div>
        <div className="mt-4">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="service-address-toggle-save"
            className="px-4 py-2 text-sm bg-[#0F4C81] hover:bg-[#0A365C] disabled:opacity-50 text-white rounded-md font-medium"
          >{saving ? "Saving…" : "Save"}</button>
          {savedAt && <span className="ml-2 text-xs text-emerald-700">Saved</span>}
        </div>
      </div>

      {/* Brand colour */}
      <div data-testid="brand-color-panel" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-6">
        <h3 className="font-display font-bold text-lg mb-1 text-slate-900 dark:text-slate-100">Brand colour</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Used for headings, action buttons, and the AMOUNT DUE accent on invoices.</p>

        <div className="flex items-center gap-3 flex-wrap mb-4">
          {PRESET_COLORS.map((c) => (
            <button
              key={c.hex}
              onClick={() => pickColor(c.hex)}
              title={c.label}
              data-testid={`brand-color-preset-${c.hex.slice(1)}`}
              className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-110 ${color.toLowerCase() === c.hex.toLowerCase() ? "border-slate-900 dark:border-white shadow-md scale-110" : "border-slate-200 dark:border-slate-700"}`}
              style={{ backgroundColor: c.hex }}
              aria-label={c.label}
            />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium">Custom hex</label>
          <input
            type="color"
            value={color}
            onChange={(e) => pickColor(e.target.value)}
            data-testid="brand-color-picker"
            className="w-10 h-10 rounded cursor-pointer border border-slate-300 dark:border-slate-700"
          />
          <input
            type="text"
            value={color}
            onChange={(e) => pickColor(e.target.value)}
            data-testid="brand-color-hex-input"
            className="w-32 px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-700 rounded-md focus:outline-none focus:border-[#0F4C81] bg-white dark:bg-slate-900"
            placeholder="#0F4C81"
          />
        </div>

        {/* Live sample */}
        <div className="mt-5 p-4 rounded border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-medium mb-2">Live preview</div>
          <div className="flex items-baseline justify-between max-w-md">
            <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">AMOUNT DUE</span>
            <span className="font-display text-2xl font-bold tabular" style={{ color }}>$282.50</span>
          </div>
          <div className="mt-3 flex gap-2">
            <button className="px-4 py-2 text-white text-sm rounded-full font-semibold" style={{ backgroundColor: color }}>Sample button</button>
            <button className="px-4 py-2 border text-sm rounded-full font-medium" style={{ borderColor: color, color }}>Outlined</button>
          </div>
        </div>
      </div>

      {/* Logo */}
      <div data-testid="logo-panel" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-6">
        <h3 className="font-display font-bold text-lg mb-1 text-slate-900 dark:text-slate-100">Business logo</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Displayed top-left on every invoice PDF and the Send-Invoice preview. PNG or JPG, transparent background recommended, ≥400×400 px for crisp print.</p>

        <div className="flex items-start gap-6 flex-wrap">
          <div className="w-40 h-40 rounded border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex items-center justify-center" data-testid="logo-preview">
            {settings?.logo_path ? (
              <img src={fileUrl("/settings/logo")} alt="Business logo" className="max-w-full max-h-full object-contain" />
            ) : (
              <ImageIcon size={36} className="text-slate-300 dark:text-slate-700" />
            )}
          </div>
          <div className="flex-1 min-w-[240px] space-y-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => uploadLogo(e.target.files?.[0])}
              data-testid="logo-upload-input"
              className="hidden"
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="logo-upload-btn"
              className="px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> {settings?.logo_path ? "Replace logo" : "Upload logo"}</>}
            </button>
            {settings?.logo_path && (
              <button
                onClick={removeLogo}
                disabled={uploading}
                data-testid="logo-remove-btn"
                className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-md font-medium flex items-center gap-2"
              >
                <X size={14} /> Remove logo
              </button>
            )}
            {uploadErr && <div className="text-xs text-red-600" data-testid="logo-upload-error">{uploadErr}</div>}
          </div>
        </div>
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-0 bg-slate-50 dark:bg-slate-950 -mx-6 px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end gap-3">
        {savedAt && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved</span>}
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-configuration-btn"
          className="px-6 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-full font-semibold flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save changes</>}
        </button>
      </div>
    </div>
  );
}

/* --------------- Stub tabs (to be built in Item 9) --------------- */

function StubTab({ tab }) {
  const links = {
    automations: [
      { label: "Invoice Reminder Schedule", to: "/settings" },
      { label: "Weekly Payday Email", to: "/settings" },
    ],
    email_sms: [
      { label: "Email templates & delivery", to: "/email-settings" },
    ],
  }[tab] || [];
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-8" data-testid={`stub-tab-${tab}`}>
      <p className="text-sm text-slate-600 dark:text-slate-400">
        This tab will be rebuilt in a future update. In the meantime, the corresponding settings live at:
      </p>
      <ul className="mt-4 space-y-2">
        {links.map((l) => (
          <li key={l.to + l.label}>
            <Link to={l.to} className="inline-flex items-center gap-1.5 text-[#0F4C81] hover:underline font-medium text-sm">
              {l.label} <ExternalLink size={13} />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
